import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SketchBox, SketchButton } from '../sketchy';
import {
  OnboardingMiniPreview,
  PreviewTrackTile,
  type OnboardingPreviewKey,
} from './OnboardingMiniPreviews';
import { WORKFLOW_STEP_KEYS, type WorkflowStepKey } from './onboardingConstants';

type SectionVariant = 'default' | 'compact' | 'slide';

function sectionVariant(compact?: boolean, variant?: SectionVariant): SectionVariant {
  if (variant) return variant;
  return compact ? 'compact' : 'default';
}

export const OnboardingHero: React.FC<{
  onSkip: () => void;
  centered?: boolean;
  compact?: boolean;
  variant?: SectionVariant;
}> = ({ onSkip, centered = true, compact = false, variant: variantProp }) => {
  const { t } = useTranslation();
  const variant = sectionVariant(compact, variantProp);
  const isSlide = variant === 'slide';
  const isCompact = variant === 'compact';

  return (
    <section className={centered ? 'text-center' : 'text-left'}>
      <div
        className={`sticky-note inline-block rotate-[-1deg] ${
          isSlide
            ? 'text-base px-4 py-2 mb-4'
            : isCompact
              ? 'text-xs px-2.5 py-0.5 mb-1.5'
              : 'text-sm mb-3 px-4 py-2'
        }`}
      >
        {t('onboarding.badge')}
      </div>
      <div className={centered ? 'flex justify-center' : ''}>
        <button
          type="button"
          onClick={onSkip}
          className={`block text-[var(--color-muted)] hover:text-[var(--color-text)] hover:underline underline-offset-2 ${
            isSlide ? 'text-base mb-4' : isCompact ? 'text-xs mb-1.5' : 'text-sm mb-3'
          }`}
        >
          {t('onboarding.skip')}
        </button>
      </div>
      <h1
        className={`font-bold scribble-underline inline-block leading-tight ${
          isSlide ? 'text-4xl xl:text-5xl' : isCompact ? 'text-3xl' : 'text-3xl sm:text-4xl'
        }`}
      >
        {t('onboarding.hero.title')}
      </h1>
      <p
        className={`text-[var(--color-muted)] leading-relaxed ${
          isSlide ? 'mt-4 text-lg xl:text-xl max-w-3xl mx-auto' : isCompact ? 'mt-1.5 text-xs' : 'mt-3 text-base'
        } ${!centered && !isSlide ? 'lg:max-w-xl' : ''}`}
      >
        {t('onboarding.hero.subtitle')}
      </p>
      <p
        className={`text-[var(--color-muted)] leading-relaxed opacity-90 ${
          isSlide ? 'mt-3 text-base xl:text-lg max-w-2xl mx-auto' : isCompact ? 'mt-1 text-[10px]' : 'mt-2 text-sm'
        }`}
      >
        {t('onboarding.hero.tagline')}
      </p>
    </section>
  );
};

export const OnboardingWhatIs: React.FC<{
  compact?: boolean;
  tilt?: 'left' | 'right';
  variant?: SectionVariant;
}> = ({ compact = false, tilt = 'left', variant: variantProp }) => {
  const { t } = useTranslation();
  const variant = sectionVariant(compact, variantProp);
  const isSlide = variant === 'slide';
  const isCompact = variant === 'compact';

  return (
    <SketchBox tilt={tilt} className={isSlide ? 'p-6 xl:p-8' : isCompact ? 'p-2.5' : 'p-4'}>
      <h2 className={`font-semibold ${isSlide ? 'text-2xl xl:text-3xl mb-3' : isCompact ? 'text-sm mb-1' : 'text-xl mb-2'}`}>
        {t('onboarding.whatIs.title')}
      </h2>
      <p
        className={`text-[var(--color-muted)] leading-relaxed ${
          isSlide ? 'text-base xl:text-lg' : isCompact ? 'text-[10px] leading-snug' : 'text-sm'
        }`}
      >
        {t('onboarding.whatIs.body')}{' '}
        <span className="opacity-90">{t('onboarding.whatIs.notLine')}</span>
      </p>
    </SketchBox>
  );
};

export const OnboardingWorkflowStep: React.FC<{
  stepKey: WorkflowStepKey;
  index: number;
  compact?: boolean;
  variant?: SectionVariant;
}> = ({ stepKey, index, compact = false, variant: variantProp }) => {
  const { t } = useTranslation();
  const variant = sectionVariant(compact, variantProp);
  const isSlide = variant === 'slide';
  const isCompact = variant === 'compact';
  const preview = t(`onboarding.workflow.steps.${stepKey}.preview`) as OnboardingPreviewKey;
  const previewSize = isSlide ? 'slide' : isCompact ? 'desktop' : 'mobile';

  return (
    <SketchBox
      tilt={index % 2 ? 'right' : 'left'}
      className={`flex flex-col h-full min-h-0 ${isSlide ? 'p-6 xl:p-8 gap-6' : isCompact ? 'p-2' : 'p-3'}`}
    >
      <div className={`flex shrink-0 ${isSlide ? 'gap-5 items-start' : isCompact ? 'gap-1.5' : 'gap-3'}`}>
        <span className={`shrink-0 ${isSlide ? 'text-5xl' : isCompact ? 'text-base' : 'text-2xl'}`} aria-hidden>
          {t(`onboarding.workflow.steps.${stepKey}.icon`)}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className={`font-semibold leading-tight ${isSlide ? 'text-2xl xl:text-3xl' : isCompact ? 'text-[11px]' : 'text-lg'}`}>
            {t(`onboarding.workflow.steps.${stepKey}.title`)}
          </h3>
          <p
            className={`text-[var(--color-muted)] leading-relaxed ${
              isSlide ? 'text-base xl:text-lg mt-3' : isCompact ? 'text-[10px] mt-0.5 leading-snug' : 'text-sm mt-2'
            }`}
          >
            {t(`onboarding.workflow.steps.${stepKey}.body`)}
          </p>
          <span
            className={`label-typewriter inline-block border border-dashed ${
              isSlide ? 'mt-4 px-3 py-1 text-sm' : isCompact ? 'mt-1 px-1.5 py-px text-[8px]' : 'mt-2 px-2 py-0.5 text-xs'
            }`}
          >
            {t(`onboarding.workflow.steps.${stepKey}.chip`)}
          </span>
        </div>
      </div>
      <div
        className={`shrink-0 text-center ${isSlide ? 'max-w-md mx-auto w-full' : isCompact ? '' : 'mx-auto max-w-sm w-full'}`}
      >
        <p
          className={`label-typewriter text-[var(--color-muted)] ${
            isSlide ? 'text-xs mb-2' : isCompact ? 'text-[7px] mb-0.5' : 'text-[9px] mb-1'
          }`}
        >
          {t('onboarding.workflow.inAppLabel')}
        </p>
        <OnboardingMiniPreview variant={preview} size={previewSize} />
      </div>
    </SketchBox>
  );
};

export const OnboardingWorkflowSection: React.FC<{ compact?: boolean; grid?: boolean }> = ({
  compact = false,
  grid = false,
}) => {
  const { t } = useTranslation();

  if (grid) {
    return (
      <section className="flex-1 min-h-0 flex flex-col">
        <h2 className={`font-semibold scribble-underline inline-block shrink-0 ${compact ? 'text-sm' : 'text-xl'}`}>
          {t('onboarding.workflow.title')}
        </h2>
        <p
          className={`text-[var(--color-muted)] shrink-0 ${compact ? 'text-[10px] mt-0.5 mb-2 leading-snug' : 'text-sm mb-4 mt-1'}`}
        >
          {t('onboarding.workflow.intro')}
        </p>
        <div className="grid grid-cols-3 grid-rows-2 gap-2 flex-1 min-h-0">
          {WORKFLOW_STEP_KEYS.slice(0, 3).map((key, i) => (
            <OnboardingWorkflowStep key={key} stepKey={key} index={i} compact />
          ))}
          <OnboardingWorkflowStep stepKey="collaborate" index={3} compact />
          <div className="col-span-2">
            <OnboardingWorkflowStep stepKey="release" index={4} compact />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <h2 className={`font-semibold mb-1 scribble-underline inline-block ${compact ? 'text-sm' : 'text-xl'}`}>
        {t('onboarding.workflow.title')}
      </h2>
      <p className={`text-[var(--color-muted)] mb-4 leading-relaxed ${compact ? 'text-xs mb-3' : 'text-sm'}`}>
        {t('onboarding.workflow.intro')}
      </p>
      <ol className="space-y-4">
        {WORKFLOW_STEP_KEYS.map((key, i) => (
          <li key={key}>
            <OnboardingWorkflowStep stepKey={key} index={i} compact={compact} />
          </li>
        ))}
      </ol>
    </section>
  );
};

export const OnboardingDemoSection: React.FC<{
  trackName: string;
  trackArtist: string;
  compact?: boolean;
  sharedTabLabel: string;
  demoLoading?: boolean;
  demoUnavailable?: boolean;
  variant?: SectionVariant;
}> = ({ trackName, trackArtist, compact = false, sharedTabLabel, demoLoading = false, demoUnavailable = false, variant: variantProp }) => {
  const { t } = useTranslation();
  const variant = sectionVariant(compact, variantProp);
  const isSlide = variant === 'slide';
  const isCompact = variant === 'compact';

  return (
    <SketchBox
      tilt="right"
      className={isSlide ? 'p-6 xl:p-8' : isCompact ? 'p-3 flex-1 min-h-0 flex flex-col' : 'p-4'}
    >
      <h2 className={`font-semibold ${isSlide ? 'text-2xl xl:text-3xl mb-3' : isCompact ? 'text-sm mb-1' : 'text-lg mb-2'}`}>
        {t('onboarding.demo.title')}
      </h2>
      <p
        className={`text-[var(--color-muted)] leading-relaxed ${
          isSlide ? 'text-base xl:text-lg' : isCompact ? 'text-[10px] leading-snug flex-1' : 'text-sm'
        }`}
      >
        {demoLoading ? (
          t('onboarding.demo.loading')
        ) : demoUnavailable ? (
          t('onboarding.demo.unavailable')
        ) : (
          <Trans
            i18nKey="onboarding.demo.body"
            values={{ trackName, sharedTab: sharedTabLabel }}
            components={[
              <strong key="track" className="text-[var(--color-text)]" />,
              <strong key="tab" className="text-[var(--color-text)]" />,
            ]}
          />
        )}
      </p>
      <div className={isSlide ? 'mt-6 max-w-md' : isCompact ? 'mt-2 shrink-0' : 'mt-3'}>
        {demoLoading ? (
          <div
            className={`animate-pulse rounded-lg bg-[var(--color-hover)] ${
              isSlide ? 'h-24' : isCompact ? 'h-14' : 'h-16'
            }`}
            aria-hidden
          />
        ) : demoUnavailable ? (
          <p className={`text-[var(--color-muted)] italic ${isSlide ? 'text-base' : 'text-sm'}`}>
            {t('onboarding.demo.emptyHint')}
          </p>
        ) : (
          <PreviewTrackTile
            name={trackName}
            artist={trackArtist}
            status={t('onboarding.demo.trackStatus')}
            sharedViewer
            compact={isCompact}
            large={isSlide}
          />
        )}
      </div>
    </SketchBox>
  );
};

export const OnboardingNextSection: React.FC<{ compact?: boolean; variant?: SectionVariant }> = ({
  compact = false,
  variant: variantProp,
}) => {
  const { t } = useTranslation();
  const variant = sectionVariant(compact, variantProp);
  const isSlide = variant === 'slide';
  const isCompact = variant === 'compact';
  const items = ['demo', 'create', 'skip'] as const;

  return (
    <SketchBox tilt="left" className={isSlide ? 'p-6 xl:p-8' : isCompact ? 'p-3 shrink-0' : 'p-4'}>
      <h2 className={`font-semibold ${isSlide ? 'text-2xl xl:text-3xl mb-4' : isCompact ? 'text-sm mb-1' : 'text-lg mb-2'}`}>
        {t('onboarding.next.title')}
      </h2>
      {isSlide ? (
        <ul className="text-base xl:text-lg text-[var(--color-muted)] space-y-3 leading-relaxed">
          {items.map((key) => (
            <li key={key}>
              <strong className="text-[var(--color-text)]">{t(`onboarding.next.${key}.label`)}</strong>
              {' — '}
              {t(`onboarding.next.${key}.desc`)}
            </li>
          ))}
        </ul>
      ) : isCompact ? (
        <ul className="text-[10px] text-[var(--color-muted)] space-y-1 leading-snug">
          {items.map((key) => (
            <li key={key}>
              <strong className="text-[var(--color-text)]">{t(`onboarding.next.${key}.label`)}</strong>
              {' — '}
              {t(`onboarding.next.${key}.desc`)}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-[var(--color-muted)] leading-relaxed space-y-1">
          {items.map((key) => (
            <span key={key} className="block">
              <strong className="text-[var(--color-text)]">{t(`onboarding.next.${key}.label`)}</strong>
              {' — '}
              {t(`onboarding.next.${key}.desc`)}
            </span>
          ))}
        </p>
      )}
    </SketchBox>
  );
};

export const OnboardingCtaButtons: React.FC<{
  onDemo: () => void;
  onCreate: () => void;
  onSkip?: () => void;
  demoLoading?: boolean;
  demoUnavailable?: boolean;
  layout?: 'stack' | 'row';
  large?: boolean;
}> = ({ onDemo, onCreate, onSkip, demoLoading = false, demoUnavailable = false, layout = 'stack', large = false }) => {
  const { t } = useTranslation();
  const demoDisabled = demoLoading || demoUnavailable;
  const demoTitle = demoLoading
    ? t('onboarding.demo.loading')
    : demoUnavailable
      ? t('onboarding.demo.unavailable')
      : undefined;
  if (layout === 'row') {
    return (
      <div className="flex items-center gap-4 justify-between flex-wrap">
        {onSkip ? (
          <button
            type="button"
            onClick={onSkip}
            className="text-base text-[var(--color-muted)] hover:text-[var(--color-text)] hover:underline underline-offset-2 shrink-0"
            data-testid="onboarding-skip-dashboard"
          >
            {t('onboarding.cta.skip')}
          </button>
        ) : (
          <span aria-hidden />
        )}
        <div className="flex items-center gap-4 justify-end flex-wrap ml-auto">
          <SketchButton
            type="button"
            variant="primary"
            onClick={onDemo}
            disabled={demoDisabled}
            title={demoTitle}
            className={`shrink-0 ${large ? 'px-8 py-3 text-lg' : 'px-6 py-2 text-base'}`}
          >
            {t('onboarding.cta.demo')}
          </SketchButton>
          <SketchButton
            type="button"
            onClick={onCreate}
            className={`shrink-0 ${large ? 'px-8 py-3 text-lg' : 'px-6 py-2 text-base'}`}
          >
            {t('onboarding.cta.create')}
          </SketchButton>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <SketchButton
        type="button"
        variant="primary"
        onClick={onDemo}
        disabled={demoDisabled}
        title={demoTitle}
        className="w-full py-2.5 text-lg"
      >
        {t('onboarding.cta.demo')}
      </SketchButton>
      <SketchButton type="button" onClick={onCreate} className="w-full py-2 text-base">
        {t('onboarding.cta.create')}
      </SketchButton>
      {onSkip ? (
        <button
          type="button"
          onClick={onSkip}
          className="w-full py-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)] hover:underline underline-offset-2"
          data-testid="onboarding-skip-dashboard"
        >
          {t('onboarding.cta.skip')}
        </button>
      ) : null}
    </div>
  );
};
